// src/frontend/components/ReceiptEditWorkspace.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Split from 'split.js';
import './ReceiptEditWorkspace.css';
import { FaEdit, FaSave, FaTimes, FaPlus, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { formatLocationLabel } from '../utils/locationLabel';

const API = import.meta.env.VITE_API_URL;

/* ---------------- helpers ---------------- */
function isJsonResponse(r) {
  const ct = r.headers.get('content-type') || '';
  return ct.includes('application/json');
}
async function safeJson(r) {
  if (!r) return null;
  if (r.status === 204) return null;
  if (isJsonResponse(r)) return r.json();
  try { await r.text(); } catch {}
  return null;
}

const toMoney = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return '0.00';
  return n.toFixed(2);
};

// --- money input helpers ---
const sanitizeMoney = (val) => {
  let s = String(val ?? '');
  // keep digits and dots only
  s = s.replace(/[^\d.]/g, '');
  // keep only the first dot
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  // limit to 2 decimals
  const parts = s.split('.');
  if (parts[1]) parts[1] = parts[1].slice(0, 2);
  s = parts.join('.');
  return s;
};
const normalizeMoney = (val) => {
  const s = sanitizeMoney(val);
  const n = Number(s);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

/* ======================================================= */

export default function ReceiptEditWorkspace({ receipt, editor }) {
  const receiptId   = Number(receipt?.receiptid || receipt?.id);
  const clientId    = Number(receipt?.clientid || 0);
  const warehouseId = Number(receipt?.warehouseid || 0);

  // DOM refs for Split.js
  const topBlockRef    = useRef(null);
  const bottomBlockRef = useRef(null);
  const leftRef        = useRef(null);
  const midRef         = useRef(null);
  const rightRef       = useRef(null);

  // Data panes
  const [history, setHistory]         = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [inventory, setInventory]     = useState([]);
  // Bottom inventory sort state (persisted)
  const INV_SORT_FIELD_KEY = 'rew.inv.sortField';
  const INV_SORT_ORDER_KEY = 'rew.inv.sortOrder';
  const getInitInvSortField = () => {
    try { return localStorage.getItem(INV_SORT_FIELD_KEY) || 'recvdate'; } catch { return 'recvdate'; }
  };
  const getInitInvSortOrder = () => {
    try { return localStorage.getItem(INV_SORT_ORDER_KEY) || 'DESC'; } catch { return 'DESC'; }
  };
  const [invSortField, setInvSortField] = useState(getInitInvSortField());
  const [invSortOrder, setInvSortOrder] = useState(getInitInvSortOrder());
  useEffect(() => { try { localStorage.setItem(INV_SORT_FIELD_KEY, invSortField); } catch {} }, [invSortField]);
  useEffect(() => { try { localStorage.setItem(INV_SORT_ORDER_KEY, invSortOrder); } catch {} }, [invSortOrder]);

  // Charges (combined)
  const [combinedLines, setCombinedLines] = useState([]);
  const [combinedTotal, setCombinedTotal] = useState(0);
  const [fees, setFees] = useState([]); // available receipt fees (clientreceiptfees)
  // Client info (for weekly storage billing check)
  const [clientInfo, setClientInfo] = useState(null);
  const [skipProratedStorage, setSkipProratedStorage] = useState(false);

  // Loading / errors
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg]   = useState('');

  // Inline edit state for charges
  const [editRow, setEditRow] = useState(null); // charge row id currently editing
  const [editDraft, setEditDraft] = useState({ qty: 1, amt: 0, custom: '', mode: 'name' });

  // Inline Custom Charge (Orders-style)
  const [customName, setCustomName] = useState('');
  const [customQty, setCustomQty]   = useState(1);
  const [customAmt, setCustomAmt]   = useState(0);

  // Per-row qty for Available Receipt Charges
  const [qtyByFee, setQtyByFee] = useState({}); // { [feeId]: number }

  // local-storage splitter persistence
  const VKEY = 'rew-vert-sizes';
  const HKEY = 'rew-horiz-sizes';

  const canLoad = useMemo(() => Number.isFinite(receiptId) && receiptId > 0, [receiptId]);

  /* ---------------- fetchers ---------------- */
  const fetchHistory = useCallback(async () => {
    if (!canLoad) return;
    try {
      const r = await fetch(`${API}/api/receipts/${receiptId}/history`);
      if (!r.ok) throw new Error();
      const json = await safeJson(r);
      setHistory(Array.isArray(json) ? json : []);
    } catch {
      setHistory([]);
      setErrMsg('Failed to load history.');
    }
  }, [canLoad, receiptId]);

  const fetchAttachments = useCallback(async () => {
    if (!canLoad) return;
    try {
      const r = await fetch(`${API}/api/receipts/${receiptId}/attachments`);
      if (!r.ok) throw new Error();
      const json = await safeJson(r);
      setAttachments(Array.isArray(json) ? json : []);
    } catch {
      setAttachments([]);
      setErrMsg('Failed to load attachments.');
    }
  }, [canLoad, receiptId]);

  const fetchInventory = useCallback(async () => {
    if (!canLoad) return;
    try {
      const r = await fetch(`${API}/api/inventory/by-receipt/${receiptId}`);
      if (!r.ok) throw new Error();
      const json = await safeJson(r);
      setInventory(Array.isArray(json) ? json : []);
    } catch {
      setInventory([]);
      setErrMsg('Failed to load inventory.');
    }
  }, [canLoad, receiptId]);

  // Sorted inventory rows (like Receipts.jsx)
  const sortedInventory = useMemo(() => {
    if (!Array.isArray(inventory) || !inventory.length) return [];
    const rows = [...inventory];
    const dir = invSortOrder === 'ASC' ? 1 : -1;
    rows.sort((a, b) => {
      const A = a[invSortField];
      const B = b[invSortField];
      const aVal = typeof A === 'number' ? A : (Date.parse(A) || (A ?? ''));
      const bVal = typeof B === 'number' ? B : (Date.parse(B) || (B ?? ''));
      if (aVal > bVal) return dir;
      if (aVal < bVal) return -dir;
      return 0;
    });
    return rows;
  }, [inventory, invSortField, invSortOrder]);

  // Totals for bottom Inventory table
  const invTotals = useMemo(() => {
    try {
      const list = Array.isArray(inventory) ? inventory : [];
      const sum = (field) => list.reduce((acc, r) => acc + (Number(r?.[field]) || 0), 0);
      return {
        qtyunits: sum('qtyunits'),
        qtyperunit: sum('qtyperunit'),
        qtyunitsonhand: sum('qtyunitsonhand'),
        piecesonhand: sum('piecesonhand')
      };
    } catch {
      return { qtyunits: 0, qtyperunit: 0, qtyunitsonhand: 0, piecesonhand: 0 };
    }
  }, [inventory]);

  const handleInvSort = (field, e) => {
    if (!field) return;
    if (invSortField === field) setInvSortOrder(invSortOrder === 'ASC' ? 'DESC' : 'ASC');
    else { setInvSortField(field); setInvSortOrder('ASC'); }
  };
  const renderInvSortIcon = (field) => (
    invSortField !== field
      ? <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />
      : (invSortOrder === 'ASC'
        ? <FaSortUp style={{ color: 'var(--primary-color)', marginLeft: 4 }} />
        : <FaSortDown style={{ color: 'var(--primary-color)', marginLeft: 4 }} />)
  );

  // NEW: combined charges (storage first, then saved receipt charges)
  const fetchChargesCombined = useCallback(async () => {
    if (!canLoad) return;
    try {
      const r = await fetch(`${API}/api/receipts/${receiptId}/charges/combined`);
      if (!r.ok) throw new Error();
      const json = await safeJson(r);
      const lines = Array.isArray(json?.lines) ? json.lines : [];
      setCombinedLines(lines);
      setCombinedTotal(Number(json?.total || 0));
    } catch {
      setCombinedLines([]);
      setCombinedTotal(0);
      setErrMsg('Failed to load combined charges.');
    }
  }, [canLoad, receiptId]);

  const fetchFees = useCallback(async () => {
    if (!clientId || !warehouseId) { setFees([]); return; }
    try {
      const url = `${API}/api/receipts/clientreceiptfees?clientid=${clientId}&warehouseid=${warehouseId}&deleted=0`;
      const r = await fetch(url);
      if (!r.ok) throw new Error();
      const json = await safeJson(r);
      setFees(Array.isArray(json) ? json : (Array.isArray(json?.data) ? json.data : []));
    } catch {
      setFees([]);
      setErrMsg('Failed to load available receipt fees.');
    }
  }, [clientId, warehouseId, canLoad]);

  // Load client to determine storage billing frequency (3 = Weekly)
  const fetchClientInfo = useCallback(async () => {
    if (!clientId) { setClientInfo(null); setSkipProratedStorage(false); return; }
    try {
      const r = await fetch(`${API}/api/clients/${clientId}`);
      if (!r.ok) throw new Error();
      const json = await safeJson(r);
      setClientInfo(json || null);
      const freq = Number(json?.monthlystoragebillingfreq ?? json?.monthlyStorageBillingFreq ?? json?.storagebillingfreq);
      setSkipProratedStorage(freq === 3);
    } catch {
      setClientInfo(null);
      setSkipProratedStorage(false);
    }
  }, [clientId]);

  /* -------------- initial data -------------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!canLoad) return;
      setLoading(true);
      setErrMsg('');
      await Promise.all([
        fetchHistory(),
        fetchAttachments(),
        fetchInventory(),
        fetchChargesCombined(), // uses combined endpoint (storage first)
        fetchFees(),
        fetchClientInfo(),
      ]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [canLoad, fetchHistory, fetchAttachments, fetchInventory, fetchChargesCombined, fetchFees, fetchClientInfo]);

  /* -------------- Split.js init (vertical + horizontal) -------------- */
  useEffect(() => {
    if (!topBlockRef.current || !bottomBlockRef.current || !leftRef.current || !midRef.current || !rightRef.current) {
      return;
    }

    // restore sizes
    let vSizes = [60, 40];
    let hSizes = [50, 28, 22]; // left(editor)/mid(charges)/right(available fees)
    try {
      const v = JSON.parse(localStorage.getItem(VKEY) || 'null');
      if (Array.isArray(v) && v.length === 2) vSizes = v;
      const h = JSON.parse(localStorage.getItem(HKEY) || 'null');
      if (Array.isArray(h) && h.length === 3) hSizes = h;
    } catch {}

    const vertical = Split(
      [topBlockRef.current, bottomBlockRef.current],
      {
        direction: 'vertical',
        sizes: vSizes,
        minSize: [280, 220],
        gutterSize: 6,
        snapOffset: 0,
        onDragEnd: sizes => localStorage.setItem(VKEY, JSON.stringify(sizes)),
      }
    );

    const horizontal = Split(
      [leftRef.current, midRef.current, rightRef.current],
      {
        sizes: hSizes,
        minSize: [320, 320, 280],
        gutterSize: 6,
        snapOffset: 0,
        onDragEnd: sizes => localStorage.setItem(HKEY, JSON.stringify(sizes)),
      }
    );

    return () => { try { vertical.destroy(); } catch {} try { horizontal.destroy(); } catch {} };
  }, []);

  /* -------------- Memos -------------- */
  // Map fee id -> fee for quick lookup
  const feeById = useMemo(() => {
    const m = new Map();
    for (const f of fees) m.set(Number(f.id), f);
    return m;
  }, [fees]);

  // Storage-first lines from the combined endpoint (filter out prorated storage if client is weekly)
  const displayCharges = useMemo(() => {
    const lines = Array.isArray(combinedLines) ? combinedLines : [];
    if (!skipProratedStorage) return lines;
    return lines.filter(l => !(l?.readOnly === true || l?.__readonly === true || (typeof l?.custom === 'string' && l.custom.includes('(Prorated)'))));
  }, [combinedLines, skipProratedStorage]);
  // Recompute total from displayCharges to reflect any filtering
  const chargesTotal = useMemo(() => {
    try {
      return displayCharges.reduce((s, r) => s + (Number(r?.qty || 0) * Number(r?.amt || 0)), 0);
    } catch { return 0; }
  }, [displayCharges]);

  /* ---------------- charge CRUD ---------------- */
  const addFeeToReceipt = async (fee, qty = 1) => {
    if (!fee || !receiptId) return;
    const payload = {
      chargeid: Number(fee.id),
      qty: Number(qty) || 1,
      amt: Number(fee.amt || 0) || 0,
      custom: fee.description || null,
    };
    try {
      const r = await fetch(`${API}/api/receipts/${receiptId}/charges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await safeJson(r);
        throw new Error(j?.error || 'Failed to add charge');
      }
      // Refresh combined so the order & totals stay correct
      await fetchChargesCombined();
    } catch (e) {
      alert(e.message || 'Failed to add charge');
    }
  };

  const startEditCharge = (row) => {
    const isProrated = row.__readonly === true || row.readOnly === true;
    if (isProrated) return; // cannot edit prorated/storage lines
    const isFeeRow = Number(row.chargeid) > 0;
    setEditRow(row.id);
    setEditDraft({
      qty: Number(row.qty) || 1,
      amt: Number(row.amt) || 0,
      custom: isFeeRow ? (row.description || '') : (row.name || row.custom || ''),
      mode: isFeeRow ? 'description' : 'name',
    });
  };

  const cancelEditCharge = () => {
    setEditRow(null);
    setEditDraft({ qty: 1, amt: 0, custom: '', mode: 'name' });
  };

  const saveEditCharge = async (rowId) => {
    try {
      const r = await fetch(`${API}/api/receipts/${receiptId}/charges/${rowId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          qty: Number(editDraft.qty) || 1,
          amt: Number(editDraft.amt) || 0,
          custom: editDraft.mode === 'name' || editDraft.mode === 'description'
            ? ((editDraft.custom ?? '').trim() || null)
            : undefined,
        }),
      });
      if (!r.ok) {
        const j = await safeJson(r);
        throw new Error(j?.error || 'Failed to update charge');
      }
      await fetchChargesCombined();
      cancelEditCharge();
    } catch (e) {
      alert(e.message || 'Failed to update charge');
    }
  };

  const deleteCharge = async (rowId) => {
    if (!confirm('Remove this charge?')) return;
    try {
      const r = await fetch(`${API}/api/receipts/${receiptId}/charges/${rowId}`, { method: 'DELETE' });
      if (!r.ok) {
        const j = await safeJson(r);
        throw new Error(j?.error || 'Failed to delete charge');
      }
      await fetchChargesCombined();
    } catch (e) {
      alert(e.message || 'Failed to delete charge');
    }
  };

  const addCustomChargeInline = async () => {
    const name = String(customName || '').trim();
    const qty  = Math.max(1, Number(customQty) || 1);
    const amt  = Number(customAmt) || 0;

    if (!name) {
      alert('Please enter a description for the custom charge.');
      return;
    }

    try {
      const r = await fetch(`${API}/api/receipts/${receiptId}/charges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom: name, qty, amt }),
      });
      if (!r.ok) {
        const j = await safeJson(r);
        throw new Error(j?.error || 'Failed to add custom charge');
      }
      setCustomName('');
      setCustomQty(1);
      setCustomAmt(0);
      await fetchChargesCombined();
    } catch (e) {
      alert(e.message || 'Failed to add custom charge');
    }
  };

  /* ---------------- render ---------------- */
  if (!canLoad) {
    return (
      <div className="rew-root">
        <div className="rew-card"><b>No receipt id.</b></div>
      </div>
    );
  }

  return (
    <div className="rew-root">
      {/* TOP ROW: Left (Editor), Middle (Receipt Charges), Right (Available Fees) */}
      <div ref={topBlockRef} className="rew-vertical">
        {/* Left: Editor (title shown inside editor form's title bar) */}
        <div ref={leftRef} className="rew-left rew-pane">
          <div className="rew-card">
            {errMsg ? (
              <div className="rew-card-head">
                <span className="rew-error">{errMsg}</span>
              </div>
            ) : null}
            <div className="rew-scroll">{editor}</div>
          </div>
        </div>

        {/* Middle: Receipt Charges */}
        <div ref={midRef} className="rew-middle rew-pane">
          <div className="rew-card rew-charges-card">
            <div className="rew-card-head rew-between">
              <h3 className="rew-title">Receipt Charges</h3>
              <div style={{ marginLeft: 'auto', fontWeight: 700 }}>
                Total: ${toMoney(chargesTotal)}
              </div>
            </div>

            <div className="rew-scroll rew-charges-scroll">
              <table className="rew-table rew-charges-table">
                <thead>
                  <tr>
                    <th style={{ width: 80 }} aria-label="actions"></th>
                    <th style={{ width: 80 }}>Qty</th>
                    <th style={{ minWidth: 220 }}>Charge</th>
                    <th style={{ width: 110 }}>Amt</th>
                    <th style={{ width: 130 }}>Row Total</th>
                  </tr>
                </thead>
                <tbody>
                  {displayCharges.length ? (
                    displayCharges.map((row, idx) => {
                      const fee = feeById.get(Number(row.chargeid)) || null;
                      const isFeeRow = Number(row.chargeid) > 0;
                      const feeName = fee?.name;
                      const name = row.name || feeName || (row.chargeid ? `Charge #${row.chargeid}` : 'Custom');
                      const description = isFeeRow ? String(row.description || fee?.description || '').trim() : '';
                      const descriptionTooltip = description.length ? description : undefined;
                      const isProrated = row.__readonly === true || row.readOnly === true;
                      const isEditing  = !isProrated && editRow === row.id;
                      const qtyNum = Number(row.qty || 0);
                      const amtNum = Number(row.amt || 0);
                      const total = qtyNum * amtNum;
                      const editingName = isEditing && editDraft.mode === 'name';
                      const editingDescription = isEditing && editDraft.mode === 'description';
                      const normalizedFeeName = String(feeName ?? '').trim().toLowerCase();
                      const normalizedDisplayName = String(name ?? '').trim().toLowerCase();
                      const showFeeMeta = isFeeRow && normalizedFeeName && normalizedFeeName !== normalizedDisplayName;

                      return (
                        <tr
                          key={row.id ?? `line-${idx}`}
                          onDoubleClick={() => !isProrated && startEditCharge(row)}
                        >
                          {/* actions cell: edit/save/delete/cancel */}
                          <td>
                            {isProrated ? null : (
                              <div className="rew-actions">
                                {isEditing ? (
                                  <>
                                    <button
                                      className="mini-btn"
                                      onClick={cancelEditCharge}
                                      title="Cancel edit"
                                    >
                                      ×
                                    </button>
                                    <button
                                      className="mini-btn success solid"
                                      onClick={() => saveEditCharge(row.id)}
                                      title="Save changes"
                                    >
                                      <FaSave />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="mini-btn"
                                      onClick={() => startEditCharge(row)}
                                      title="Edit charge"
                                    >
                                      <FaEdit />
                                    </button>
                                    <button
                                      className="mini-btn danger"
                                      style={{ background: 'transparent', color: '#e74c3c' }}
                                      onClick={() => deleteCharge(row.id)}
                                      title="Remove charge"
                                    >
                                      <FaTimes />
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Qty */}
                          <td style={{ width: 80 }}>
                            {isEditing ? (
                              <input
                                className="rew-input"
                                type="number"
                                min={1}
                                value={editDraft.qty}
                                onChange={e => setEditDraft(d => ({ ...d, qty: e.target.value }))}
                                style={{ width: 90 }}
                              />
                            ) : (
                              qtyNum
                            )}
                          </td>

                          {/* Charge name */}
                          <td style={{ minWidth: 220 }}>
                            {editingName ? (
                              <input
                                className="rew-input"
                                value={editDraft.custom}
                                onChange={e => setEditDraft(d => ({ ...d, custom: e.target.value }))}
                                placeholder="Charge name"
                                style={{ width: '100%' }}
                              />
                            ) : editingDescription ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div className="rew-fee-name">
                                  <strong>{name}</strong>
                                  {showFeeMeta ? (
                                    <div className="rew-fee-meta">Fee: {feeName}</div>
                                  ) : null}
                                  {isProrated ? <span className="rew-badge">auto</span> : null}
                                </div>
                                <input
                                  className="rew-input"
                                  value={editDraft.custom}
                                  onChange={e => setEditDraft(d => ({ ...d, custom: e.target.value }))}
                                  placeholder="Description"
                                />
                              </div>
                            ) : (
                              <div className="rew-fee-name" title={descriptionTooltip}>
                                <strong>{name}</strong>
                                {showFeeMeta ? (
                                  <div className="rew-fee-meta">Fee: {feeName}</div>
                                ) : null}
                                {isProrated ? <span className="rew-badge">auto</span> : null}
                              </div>
                            )}
                          </td>

                          {/* Amount */}
                          <td style={{ width: 110 }} className="money">
                            {isEditing ? (
                              <input
                                className="rew-input"
                                type="text"
                                inputMode="decimal"
                                pattern="^\\d+(\\.\\d{0,2})?$"
                                value={String(editDraft.amt ?? '')}
                                onChange={(e) => {
                                  const v = sanitizeMoney(e.target.value);
                                  setEditDraft(d => ({ ...d, amt: v }));
                                }}
                                onBlur={() => {
                                  setEditDraft(d => ({ ...d, amt: normalizeMoney(d.amt) }));
                                }}
                                style={{ width: 100 }}
                                placeholder="0.00"
                              />
                            ) : (
                              `$${toMoney(amtNum)}`
                            )}
                          </td>

                          {/* Row total */}
                          <td style={{ width: 130 }} className="money">{`$${toMoney(total)}`}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr><td colSpan={5}>No charges.</td></tr>
                  )}
                </tbody>
              </table>

              {/* REMARK: Custom charge grouped like a fieldset (temporarily hidden, keep for potential re-use) */}
              {false && (
                <fieldset className="rew-fieldset">
                  <legend>Add Custom Charge</legend>
                  <div className="rew-custom-row">
                    <input
                      className="rew-input name"
                      placeholder="Custom charge name"
                      value={customName}
                      onChange={e => setCustomName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addCustomChargeInline(); }}
                      aria-label="Custom charge name"
                    />
                    <div className="rew-field">
                      <label htmlFor="rewCustomQty" className="rew-label">Qty</label>
                      <input
                        id="rewCustomQty"
                        className="rew-input qty"
                        type="number"
                        min={1}
                        value={customQty}
                        onChange={e => setCustomQty(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addCustomChargeInline(); }}
                        placeholder="Qty"
                        aria-label="Custom quantity"
                      />
                    </div>
                    <div className="rew-field">
                      <label htmlFor="rewCustomAmt" className="rew-label">Fee Amt</label>
                      <input
                        id="rewCustomAmt"
                        className="rew-input amt"
                        type="text"
                        inputMode="decimal"
                        pattern="^\\d+(\\.\\d{0,2})?$"
                        value={String(customAmt ?? '')}
                        onChange={(e) => setCustomAmt(sanitizeMoney(e.target.value))}
                        onBlur={() => setCustomAmt(a => normalizeMoney(a))}
                        onKeyDown={(e) => { if (e.key === 'Enter') addCustomChargeInline(); }}
                        placeholder="0.00"
                        aria-label="Custom amount"
                      />
                    </div>
                    <button className="btn purple" onClick={addCustomChargeInline} title="Add custom charge">
                      <FaPlus style={{ marginRight: 6 }} />
                      Add custom
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>

        {/* Right: Available Receipt Charges (match Orders + Qty input before action) */}
        <div ref={rightRef} className="rew-right rew-pane">
          <div className="rew-card rew-available-card">
            <div className="rew-card-head rew-between"><h3 className="rew-title">Available Receipt Charges</h3></div>
            <div className="rew-scroll rew-available-scroll">
              <table className="rew-table rew-available-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th style={{ width: 120 }}>Default Rate</th>
                    <th style={{ width: 64 }}>Qty</th>
                    <th style={{ width: 42 }} aria-label="add"></th>
                  </tr>
                </thead>
                <tbody>
                  {fees.length ? (
                    fees.map(f => {
                      const qty = Number(qtyByFee[f.id] ?? 1) || 1;
                      const defaultRateNum = Number(f.amt);
                      const defaultRateText = Number.isFinite(defaultRateNum)
                        ? `$${toMoney(defaultRateNum)}`
                        : '—';
                      return (
                        <tr key={f.id}>
                          <td>
                            <div className="rew-fee-name">
                              <span>{f.name}{f.deleted ? ' (deleted)' : ''}</span>
                              {Number(f.type) !== 0 && Number(f.usevolume) === 1 ? (
                                <span className="rew-badge">volume</span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <div className="rew-fee-desc">{f.description || '—'}</div>
                          </td>
                          <td className="money">{defaultRateText}</td>
                          <td>
                            <input
                              type="number"
                              min={1}
                              value={qty}
                              onChange={e => {
                                const v = Math.max(1, Number(e.target.value) || 1);
                                setQtyByFee(prev => ({ ...prev, [f.id]: v }));
                              }}
                              style={{ width: 64 }}
                              className="rew-input"
                            />
                          </td>
                          <td>
                            <button
                              className="mini-btn"
                              onClick={() => addFeeToReceipt(f, qty)}
                              title={`Add ${qty}x "${f.name}"`}
                            >
                              +
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr><td colSpan={5}>No available fees for this client/warehouse.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM ROW: Inventory */}
      <div ref={bottomBlockRef} className="rew-bottom">
        <div className="rew-card rew-inventory-card">
          <div className="rew-card-head"><h3 className="rew-title">Inventory (Receipt #{receiptId})</h3></div>
          <div className="rew-scroll rew-inventory-scroll">
            <table className="rew-table rew-inventory-table">
              <thead>
                <tr>
                  <th onClick={(e)=>handleInvSort('pallet_number', e)} className={invSortField==='pallet_number' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='pallet_number'?'var(--primary-color)':undefined, textDecoration: invSortField==='pallet_number'?'underline':undefined }}>Pallet # {renderInvSortIcon('pallet_number')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('recvdate', e)} className={invSortField==='recvdate' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='recvdate'?'var(--primary-color)':undefined, textDecoration: invSortField==='recvdate'?'underline':undefined }}>Recv Date {renderInvSortIcon('recvdate')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('storage_device', e)} className={invSortField==='storage_device' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='storage_device'?'var(--primary-color)':undefined, textDecoration: invSortField==='storage_device'?'underline':undefined }}>Storage {renderInvSortIcon('storage_device')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('sku', e)} className={invSortField==='sku' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='sku'?'var(--primary-color)':undefined, textDecoration: invSortField==='sku'?'underline':undefined }}>SKU {renderInvSortIcon('sku')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('item_description', e)} className={invSortField==='item_description' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='item_description'?'var(--primary-color)':undefined, textDecoration: invSortField==='item_description'?'underline':undefined }}>Item Description {renderInvSortIcon('item_description')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('location', e)} className={invSortField==='location' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='location'?'var(--primary-color)':undefined, textDecoration: invSortField==='location'?'underline':undefined }}>Location {renderInvSortIcon('location')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('warehouse_area', e)} className={invSortField==='warehouse_area' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='warehouse_area'?'var(--primary-color)':undefined, textDecoration: invSortField==='warehouse_area'?'underline':undefined }}>Warehouse Area {renderInvSortIcon('warehouse_area')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('description', e)} className={invSortField==='description' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='description'?'var(--primary-color)':undefined, textDecoration: invSortField==='description'?'underline':undefined }}>Notes {renderInvSortIcon('description')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('partnumber', e)} className={invSortField==='partnumber' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='partnumber'?'var(--primary-color)':undefined, textDecoration: invSortField==='partnumber'?'underline':undefined }}>Part # {renderInvSortIcon('partnumber')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('expiration', e)} className={invSortField==='expiration' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='expiration'?'var(--primary-color)':undefined, textDecoration: invSortField==='expiration'?'underline':undefined }}>Expiration {renderInvSortIcon('expiration')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('lotnum', e)} className={invSortField==='lotnum' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='lotnum'?'var(--primary-color)':undefined, textDecoration: invSortField==='lotnum'?'underline':undefined }}>Lot # {renderInvSortIcon('lotnum')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('qtyunits', e)} className={invSortField==='qtyunits' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='qtyunits'?'var(--primary-color)':undefined, textDecoration: invSortField==='qtyunits'?'underline':undefined }}>Qty Units {renderInvSortIcon('qtyunits')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('qtyperunit', e)} className={invSortField==='qtyperunit' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='qtyperunit'?'var(--primary-color)':undefined, textDecoration: invSortField==='qtyperunit'?'underline':undefined }}>Qty Per {renderInvSortIcon('qtyperunit')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('qtyunitsonhand', e)} className={invSortField==='qtyunitsonhand' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='qtyunitsonhand'?'var(--primary-color)':undefined, textDecoration: invSortField==='qtyunitsonhand'?'underline':undefined }}>Qty On Hand {renderInvSortIcon('qtyunitsonhand')}</span>
                  </th>
                  <th onClick={(e)=>handleInvSort('piecesonhand', e)} className={invSortField==='piecesonhand' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: invSortField==='piecesonhand'?'var(--primary-color)':undefined, textDecoration: invSortField==='piecesonhand'?'underline':undefined }}>Pieces On Hand {renderInvSortIcon('piecesonhand')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedInventory.length ? sortedInventory.map(r => (
                  <tr key={r.inventoryid}>
                    <td>{r.pallet_number}</td>
                    <td>{r.recvdate ? new Date(r.recvdate).toLocaleDateString() : ''}</td>
                    <td>{r.storage_device}</td>
                    <td>{r.sku}</td>
                    <td>{r.item_description}</td>
                    <td>{formatLocationLabel({
                      location: r.location,
                      locationTypeId: r.pallet_locationtypeid ?? r.locationtypeid ?? r.locationtype ?? r.location_type ?? r.locationtypename,
                      stackLabel: r.stack,
                    })}</td>
                    <td>{r.warehouse_area}</td>
                    <td>{r.description}</td>
                    <td>{r.partnumber}</td>
                    <td>{r.expiration ? new Date(r.expiration).toLocaleDateString() : ''}</td>
                    <td>{r.lotnum}</td>
                    <td>{r.qtyunits}</td>
                    <td>{r.qtyperunit}</td>
                    <td>{r.qtyunitsonhand}</td>
                    <td>{r.piecesonhand}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={15}>No inventory rows.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td>{sortedInventory.length ? `Totals:(${Number(sortedInventory.length).toLocaleString()} found)` : ''}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ fontWeight: 700 }}>{Number(invTotals.qtyunits || 0).toLocaleString()}</td>
                  <td style={{ fontWeight: 700 }}>{Number(invTotals.qtyperunit || 0).toLocaleString()}</td>
                  <td style={{ fontWeight: 700 }}>{Number(invTotals.qtyunitsonhand || 0).toLocaleString()}</td>
                  <td style={{ fontWeight: 700 }}>{Number(invTotals.piecesonhand || 0).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {loading && <div style={{position:'absolute', inset:0, pointerEvents:'none'}} aria-hidden="true" />}
    </div>
  );
}
